import React, { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/;
const VIDEO_EXT = /\.(mp4|webm|ogg|ogv|mov|m4v)$/;

// Detects whether an attachment (or plain URL) can be shown inline as an image or video
export const getMediaKind = (urlOrAttachment) => {
  const att = typeof urlOrAttachment === 'string' ? { url: urlOrAttachment } : (urlOrAttachment || {});
  const mime = String(att.mimetype || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  const candidates = [att.url, att.filename, att.title].map(v => String(v || '').split(/[?#]/)[0].toLowerCase());
  if (candidates.some(c => IMAGE_EXT.test(c))) return 'image';
  if (candidates.some(c => VIDEO_EXT.test(c))) return 'video';
  return null;
};

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const STEP = 1.25;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Pointer position relative to the centre of the element
const pointRelativeTo = (el, e) => {
  const rect = el.getBoundingClientRect();
  return { x: e.clientX - rect.left - rect.width / 2, y: e.clientY - rect.top - rect.height / 2 };
};

// Image with zoom in/out (buttons, mouse wheel, double click) and drag-to-pan, rendered in place
export const ZoomableImage = ({ src, alt, className = 'h-[60vh]' }) => {
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Mirror of the latest zoom state so the wheel listener never reads stale values
  const viewRef = useRef({ scale: 1, offset: { x: 0, y: 0 } });
  viewRef.current = { scale, offset };

  const reset = () => { setScale(1); setOffset({ x: 0, y: 0 }); };

  useEffect(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, [src]);

  // Zooms to `next`, keeping `origin` (relative to the container centre) fixed on screen
  const zoomTo = (next, origin = { x: 0, y: 0 }) => {
    const { scale: prev, offset: o } = viewRef.current;
    const newScale = clamp(next, MIN_SCALE, MAX_SCALE);
    if (newScale === 1) { reset(); return; }
    const ratio = newScale / prev;
    setScale(newScale);
    setOffset({ x: origin.x - (origin.x - o.x) * ratio, y: origin.y - (origin.y - o.y) * ratio });
  };

  // Wheel listener must be non-passive so the page doesn't scroll while zooming
  const zoomRef = useRef(zoomTo);
  zoomRef.current = zoomTo;
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const prev = viewRef.current.scale;
      zoomRef.current(e.deltaY < 0 ? prev * STEP : prev / STEP, pointRelativeTo(el, e));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setExpanded(false); setScale(1); setOffset({ x: 0, y: 0 }); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const onPointerDown = (e) => {
    if (scale === 1) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    setDragging(true);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setOffset({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };
  const onPointerUp = () => { dragRef.current = null; setDragging(false); };

  const onDoubleClick = (e) => {
    if (scale > 1) reset();
    else zoomTo(2.5, pointRelativeTo(containerRef.current, e));
  };

  const btn = 'p-1.5 rounded bg-white/90 hover:bg-white text-[#42526E] shadow border border-[#DFE1E6] disabled:opacity-40';

  return (
    <div className={expanded ? 'fixed inset-0 z-[80] bg-black/90 flex flex-col' : 'relative'}>
      <div
        ref={containerRef}
        className={`relative overflow-hidden select-none flex items-center justify-center ${expanded ? 'flex-1' : `${className} bg-[#F4F5F7] rounded border border-[#DFE1E6]`}`}
        style={{ cursor: scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in', touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="max-w-full max-h-full object-contain"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transition: dragging ? 'none' : 'transform 0.12s ease-out' }}
        />
      </div>
      <div className={`flex items-center gap-1.5 ${expanded ? 'justify-center p-3' : 'absolute top-2 right-2'}`}>
        <button type="button" className={btn} onClick={() => zoomTo(scale / STEP)} disabled={scale <= MIN_SCALE} title="Zoom out">
          <MagnifyingGlassMinusIcon className="h-4 w-4" />
        </button>
        <button type="button" className={`${btn} text-xs font-semibold min-w-[3.25rem]`} onClick={reset} title="Reset zoom">
          {Math.round(scale * 100)}%
        </button>
        <button type="button" className={btn} onClick={() => zoomTo(scale * STEP)} disabled={scale >= MAX_SCALE} title="Zoom in">
          <MagnifyingGlassPlusIcon className="h-4 w-4" />
        </button>
        <button type="button" className={btn} onClick={() => { setExpanded(v => !v); reset(); }} title={expanded ? 'Exit full screen (Esc)' : 'Full screen'}>
          {expanded ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

// Renders an image (zoomable) or video (inline player) in place; returns null for other files
const MediaViewer = ({ src, kind, title, className }) => {
  const resolvedKind = kind || getMediaKind(src);
  if (!src || !resolvedKind) return null;
  if (resolvedKind === 'image') return <ZoomableImage src={src} alt={title || 'Attachment'} className={className} />;
  return (
    <video
      key={src}
      src={src}
      controls
      preload="metadata"
      playsInline
      className={`w-full bg-black rounded ${className || 'max-h-[60vh]'}`}
    >
      Your browser cannot play this video format.
    </video>
  );
};

export default MediaViewer;
